/**
 * App Check attestation, initialised lazily and only for the admin journey.
 *
 * ## Why this is not in the composition root
 *
 * App Check is configured per Firebase app, not per page, so calling
 * `initializeAppCheck` at start-up would load Google's reCAPTCHA script on
 * *every* page — including public retrieval. That script profiles the visitor's
 * behaviour to produce a score, which on the retrieval journey would mean
 * sending behavioural signals about a child in distress to a third party, on the
 * one page where KinCode deliberately self-hosts its icons to avoid exactly that
 * kind of request.
 *
 * So it is initialised on demand, the first time the router admits someone to an
 * authenticated route. A visitor who only ever retrieves contacts never loads it.
 *
 * ## Why the public endpoint still does not require it
 *
 * `retrieveContacts` treats a token as a trust signal and never as a gate (see
 * `functions/src/security/throttle.js`). Enforcement is enabled only for the
 * authenticated callables, where a failed attestation means "sign in again"
 * rather than "a child cannot reach their contacts".
 */

import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

/** Initialised once per page load, on first use. */
let started = false;

/**
 * Start App Check if it is configured and not already running.
 *
 * Failures are swallowed deliberately. Attestation is a defence in depth: if the
 * provider script is blocked, the network is poor, or the site key is wrong, the
 * admin journey must still work — the server rejects unattested calls only once
 * enforcement is switched on, and that switch is a deployment decision made with
 * the console open, not something a broken script should trigger silently.
 *
 * @param {import("./app.js").FirebaseContext} context
 * @param {string | undefined} siteKey
 * @returns {void}
 */
export function startAppCheck(context, siteKey) {
  if (started || !siteKey) {
    return;
  }
  started = true;

  try {
    initializeAppCheck(context.app, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      // Refresh in the background so a long admin session does not fail on an
      // expired token mid-edit.
      isTokenAutoRefreshEnabled: true,
    });
  } catch {
    // Nothing is logged: a failure here says nothing useful and the console
    // message would be the reCAPTCHA library's, not ours.
  }
}
